import { Base64 } from 'js-base64';
import * as lodash from 'lodash';
import moment from 'moment';
import _ from 'underscore';
import type {
  ClipboardEvent,
  KeyboardEvent,
  ChangeEvent as ReactChangeEvent,
  ReactElement,
  ReactNode,
  ReactNodeArray,
} from 'react';

import { IGNORE_RESOLVE_RENAME } from '../shared/featureFlags/flagNames';
import { isCustomLicense } from '../shared/licenses';
import { LOD_OPTIONS } from '../shared/organizations/lodOptions';
import { encodeReference } from '../shared/utils/encodeReference';
import nuc from '../shared/utils/nuc';
import { getHumanReadableEPSSFields } from '../shared/vulns/getHumanReadableEPSSFields';
import { Action, ResourceType } from '../typings/permissions';
import type { Category, EpssResponse } from '../shared/issuesV2';
import type { Match, RevisionLicense, RevisionLicense as RevisionLicenseModel } from '../shared/licenses/common';
import type { OrganizationAccessLevel } from '../shared/model-typings/organization';
import type { Permission } from '../typings/permissions';

import { window } from './globals';
import { isFeatureEnabled } from './helpers/features/featureFlags';
import type { RevisionRouteParams } from './components/projects/projectDashboardTypes';
import type { FossaFeature } from './components/settings/billing/features/consts';
import type { AsyncStatus, IAsyncStatus, Project, SelectOption } from './types';

export { default as nuc } from '../shared/utils/nuc';

/**
 * hasError says whether there's an error set in the status object.
 */
export const hasError = (
  status: AsyncStatus | IAsyncStatus<unknown> | (Record<string, unknown> & { errors: Error[] }),
): boolean => !_.isEmpty(status.errors) || !!status.error;

/**
 * Deterministically generate a CSS colour to "represent" a string.
 */
export function hashColor(str, transparency?) {
  if (!str) {
    return 'rgba(255,255,255,.3)';
  }
  str = str.toString();
  let seed = 0;
  for (let i = 0; i < str.length; i++) {
    seed += str.charCodeAt(i);
  }

  const index = (Math.sin(seed) + 1) * 2;
  const m = Math.floor(index);
  const rgb = (function (v) {
    return {
      0: [255, 0, v],
      1: [255 - v, 0, 255],
      2: [0, v, 255],
      3: [0, 255, 255 - v],
      4: [v, 255, 0],
      5: [255, 255 - v, 0],
    }[m];
  }(Math.floor((index - m) * 255)));
  return `rgba(${rgb.join(',')},${transparency || 1})`;
}

/**
 * Determine if a value is true.
 */
export function isTrue(val: unknown): val is true {
  const stringified = String(val);
  return stringified === 'true' || stringified === '1';
}

/**
 *
 */
export function defer(func: FrameRequestCallback) {
  // set on requestAnimationFrame to guarantee after frame has been drawn
  const requestAnimationFrame =
    window.requestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.msRequestAnimationFrame;
  setTimeout(() => {
    requestAnimationFrame(func);
  }, 1); // push this to a new async thread to get executed so it guarantees a separate frame
}

/**
 *
 */
export function stopEventPropagation(e: { stopPropagation(): void; preventDefault(): void } | null | undefined) {
  if (!e) {
    return null;
  }
  e.stopPropagation();
  e.preventDefault();
  return e;
}

/**
 *
 */
export function containEvent(func: Function) {
  return function (e: { stopPropagation(): void; preventDefault(): void }) {
    e.stopPropagation();
    e.preventDefault();
    func.apply(this, arguments);
  };
}

type ChangeEvent = ReactChangeEvent<HTMLInputElement>;

/**
 * Accepts an event handler for an input element with a checked attribute.
 * Passes event.target.checked as the first argument, so the handler
 * doesn't need to dig around for it.
 *
 * @param {(isChecked: boolean, event: ChangeEvent) => void} fn -
 */
export const withTargetChecked = (fn: (isChecked: boolean, event: ChangeEvent) => void) => (event: ChangeEvent) => {
  if (event?.target) {
    return fn(event.target.checked, event);
  }
};

/**
 * Higher-order function which returns an onPaste event handler,
 * passing the clipboard text through (if it exists).
 * Also passes the event along as the 2nd argument.
 *
 * @param {(text: string | undefined, event: ClipboardEvent<HTMLInputElement>) => void} fn - Your event handler, which recieves the text as the first argument.
 */
export const withClipboardText = (fn: (text: string | undefined, event: ClipboardEvent<HTMLInputElement>) => void) =>
  (event: ClipboardEvent<HTMLInputElement>) => fn(event?.clipboardData?.getData('text'), event);

/**
 * Takes a string and a list of words in that string to be bolded.
 * Returns a ReactNodeArray representing the entire string,
 * where any of the specified words are wrapped in a tag which bolds the text.
 *
 * @param {string} sentence - The sentence to look for bold words in.
 * @param {string[] | string} wordsToBold - The list of words to make bold.
 * @returns {ReactNodeArray}
 */
export function boldWords(sentence: string, wordsToBold: string[] | string): ReactNodeArray {
  const wordsToBoldArray = Array.isArray(wordsToBold) ? wordsToBold : wordsToBold.split(' ');
  const lowercaseWordsToBold = new Set(wordsToBoldArray.map(word => word.toLowerCase()));
  const shouldWordBeBold = (word: string) => lowercaseWordsToBold.has(word.toLowerCase());
  //  Handles rendering either the bold <span> or the word as a string (with the trailing space).
  const renderWord = (word: string, index: number, sentenceArray: string[]) => {
    //  Handle adding the trailing space, except for the final word.
    const isLastWord = (sentenceArray.length - 1) === index;
    if (shouldWordBeBold(word)) {
      return (
        <>
          <span className="bold">{word}</span>
          {isLastWord ? '' : ' '}
        </>
      );
    }
    return isLastWord ? word : `${word} `;
  };
  return sentence.split(' ').map(renderWord);
}

/**
 * Append an ordinal suffix to a number.
 * @example 1 becomes 1st, 17 becomes 17th.
 *
 * @depreated Use ./shared/utils/numbers.ts:formatOrdinals()
 */
export function numSuffix(n: number) {
  const d = (n | 0) % 100;
  return n + (d > 3 && d < 21 ? 'th' : ['th', 'st', 'nd', 'rd'][d % 10] || 'th');
}

/**
 * Clamps a string to a maximum length. Strings longer than the maximum length
 * will be truncated 3 characters before the max length and terminate with '...'.
 */
export function trimString(str: string, maxlength: number) {
  if (!str) {
    return '';
  }
  if (str.length > maxlength) {
    return `${str.substring(0, maxlength - 3)}...`;
  }
  return str.substring(0, maxlength);
}

/**
 * Truncates into a display format that uses at most 3 digits by truncating and suffixing.
 * Some examples:
 *  12345678 -> 12.3M
 *   1234567 -> 1.23M
 *    123456 -> 123K
 *     12345 -> 12.3K
 *      1234 -> 1.23K
 *       123 -> 123
 *        12 -> 12
 * Used for saving space in subnavs with lots of categories.
 * @deprecated Use ./app/utils/numbers.ts:abbrevNumber()
 */
export const truncateNumber = (num: number) => {
  // number of digits in `num`
  const digits = num.toString().length;

  // number of commas in `num` when written out: 12,345,678 -> 2
  const commas = Math.floor((digits - 1) / 3);

  // how many digits after the radix point: 12,345,678 -> 12.3M -> 1
  const trailing = 3 - digits + 3 * commas;

  // truncate with `trailing` digits after radix point: 12,345,678 -> 12.3
  const truncated = Math.round(num * 10 ** (3 - digits)) / 10 ** trailing;

  // potential suffixes, up to trillions
  const suffix = ['', 'K', 'M', 'B', 'T'][commas];

  return `${truncated}${suffix}`;
};

/**
 * Formats a number with commas as thousands separators.
 *
 * Examples:
 * 100 -> 100
 * 1000 -> 1,000
 * 1000000 -> 1,000,000
 * @deprecated Use ./shared/utils/numbers.ts:prettyNumber()
 */
export const formatNumberWithCommas = (num: number): string => {
  const chars = num.toString().split('');

  return chars.reduce((acc, char, idx) => {
    const positionFromRight = chars.length - idx;
    if (positionFromRight % 3 === 0 && idx !== 0) {
      return `${acc},${char}`;
    }
    return `${acc}${char}`;
  }, '');
};

/**
 * Check if a given version string is a full git hash.
 */
export const isFullHash = (version: string): boolean => {
  const gitHashPattern = /[0-9a-fA-F]{40}/;
  return !!version.match(gitHashPattern);
};

/**
 * **!!!Frontend Only!!!** Verify that a user has at least a certain subscription level.
 * - free
 * - premium
 * - enterprise
 */
export function verify_subscription(accessLevel: 'free' | 'premium' | 'enterprise') {
  if (!window.user) {
    return false;
  }

  if (window.on_prem) {
    return true;
  }

  if (!window.user.organization) {
    return false;
  }

  let orgAccessLevel = window.user.organization.access_level ?? 'free';
  if (orgAccessLevel === 'premium' && window.user.organization.custom_billing) {
    orgAccessLevel = 'enterprise';
  }

  const subscriptionAccessValues: Record<string, number> = {
    free: 0,
    premium: 1,
    enterprise: 2,
  };

  return subscriptionAccessValues[orgAccessLevel] >= subscriptionAccessValues[accessLevel];
}

/**
 *
 */
export function isAuthed(subscription = 'free') {
  return verify_subscription(subscription);
}

/**
 * Verify that the current user belongs to the organization that owns the given project.
 * This check is used to hide certain ProjectSubnav items (ie Settings, Issues) from
 * users who belong to different orgs but have "view any project" permissions. They should
 * be shown the "public" project view, which shows some stuff but is read-only.
 */
export function isProjectInCurrentOrg(projectOrgId: Project['organizationId']) {
  return window.user?.organizationId === projectOrgId;
}

/**
 * Capitalize the first letter of the given string.
 */
export function capitalizeWord(word: string) {
  if (!word) {
    return '';
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/**
 * Turn a snake cased word into multiple capitalized words separated by spaces
 * @example capitalizeSnakeCaseWord('thisIsReallyCool') // 'This Is Really Cool'
 */
export function capitalizeSnakeCaseWord(word: string) {
  if (!word) {
    return '';
  }
  return word.split('_').map(capitalizeWord).join(' ');
}

/**
 * Remove HTML from a string.
 * @deprecated
 */
export function stripHtml(str : string) {
  return (str || '').replace(/<(?:.|\n)*?>/gm, '');
}

/**
 * @deprecated Use the React component instead.
 */
export function requirePremium<F extends () => void>(f: F, focusFeature: FossaFeature): F {
  return !verify_subscription('premium') ? ((() => window.showPlanSelectorModal({ focusFeature })) as F) : f;
}

export const validArchiveUploadExtensions: readonly string[] = Object.freeze([
  '.apk',
  '.bz2',
  '.bzip2',
  '.conda',
  '.gem',
  '.gz',
  '.ipa',
  '.jar',
  '.nupkg',
  '.rpm',
  '.tar',
  '.whl',
  '.xz',
  '.zip',
].sort());

export const archiveUploadAccept: Record<string, string[]> = Object.freeze({
  'application/gzip': ['.gz'],
  'application/java-archive': ['.jar'],
  'application/vnd.android.package-archive': ['.apk'],
  'application/x-bzip2': ['.bz2', '.bzip2'],
  'application/x-tar': ['.gem', '.tar'],
  'application/zip': ['.conda', '.zip', '.nupkg'],
  'application/x-pywheel+zip': ['.whl'],
  'application/x-rpm': ['.rpm'],
  'application/octet-stream': [
    '.conda',
    '.ipa',
    '.xz',
  ],
});

export const validSBOMUploadExtensions: readonly string[] = Object.freeze([
  '.json',
  '.xml',
].sort());

export const sbomUploadAccept: Record<string, string[]> = Object.freeze({
  'application/json': ['.json'],
  'application/xml': ['.xml'],
});

export const validCodeSentryArchiveUploadExtensions: readonly string[] = Object.freeze([
  '.7z',
  '.a',
  '.apk',
  '.ar',
  '.arj',
  '.bz2',
  '.cab',
  '.class',
  '.cramfs',
  '.deb',
  '.dex',
  '.dll',
  '.dmg',
  '.exe',
  '.ext2',
  '.ext3',
  '.ext4',
  '.fat',
  '.gz',
  '.gzip',
  '.hex',
  '.ima',
  '.ipa',
  '.iso',
  '.jar',
  '.js',
  '.lib',
  '.lrz',
  '.lz',
  '.lzma',
  '.lzo',
  '.mbr',
  '.msi',
  '.msp',
  '.msu',
  '.npm',
  '.o',
  '.obj',
  '.odex',
  '.ova',
  '.pax',
  '.pkg',
  '.py',
  '.python',
  '.rar',
  '.rpm',
  '.rz',
  '.sfs',
  '.sqf',
  '.sqfs',
  '.sqs',
  '.squ',
  '.squashfs',
  '.so',
  '.swmnote',
  '.tar',
  '.tar.gz',
  '.tbz',
  '.tbz2',
  '.tgz',
  '.txz',
  '.upx',
  '.vsix',
  '.whl',
  '.wim',
  '.vmdk',
  '.xar',
  '.xpi',
  '.xz',
  '.zip',
  'AppxManifest.xml',
].sort());

export const codeSentryArchiveUploadAccept: Record<string, string[]> = Object.freeze({
  'application/gzip': ['.gz', '.tgz', '.tar.gz'],
  'application/java-archive': ['.jar', '.class'],
  'application/javascript': ['.js'],
  'application/vnd.android.package-archive': ['.apk'],
  'application/x-dex': ['.dex', '.odex'],
  'application/x-7z-compressed': ['.7z'],
  'application/x-bzip2': ['.bz2', '.tbz', '.tbz2'],
  'application/x-debian-package': ['.deb'],
  'application/x-archive': ['.a', '.ar', '.lib'],
  'application/x-arj': ['.arj'],
  'application/x-microsoft-cabinet': ['.cab'],
  'application/x-msi': ['.msi', '.msp', '.msu'],
  'application/x-cpio': ['.cpio'],
  'application/x-cramfs': ['.cramfs'],
  'application/x-iso9660-image': ['.iso'],
  'application/x-lrzip': ['.lrz'],
  'application/x-lzip': ['.lz'],
  'application/x-lzma': ['.lzma'],
  'application/x-lzop': ['.lzo'],
  'application/x-apple-diskimage': ['.dmg'],
  'application/x-rar-compressed': ['.rar'],
  'application/x-redhat-package-manager': ['.rpm'],
  'application/x-rzip': ['.rz'],
  'application/x-squashfs': ['.sqsh', '.squashfs', '.sfs', '.sqf', '.sqfs', '.sqs', '.squ'],
  'application/x-tar': ['.tar', '.ova'],
  'application/x-vmdk': ['.vmdk'],
  'application/x-ms-wim': ['.wim'],
  'application/x-xar': ['.xar', '.pkg'],
  'application/x-xz': ['.xz', '.txz'],
  'application/zip': ['.zip', '.vsix', '.xpi', '.whl'],
  'application/octet-stream': [
    '.dll',
    '.exe',
    '.ext2',
    '.ext3',
    '.ext4',
    '.fat',
    '.hex',
    '.ima',
    '.ipa',
    '.mbr',
    '.o',
    '.pax',
    '.so',
    '.swmnote',
    '.upx',
  ],
  'text/x-python': ['.py', '.python'],
  'text/x-obj': ['.obj'],
  'text/xml': ['AppxManifest.xml'],
});

/**
 * Returns an onKeyPress handler which calls the
 * provided function if the Escape key is pressed
 */
export const withEscKeyPress = (fn: (event: KeyboardEvent) => void) => {
  const handler = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      return fn(event); // Pass the event along, in case the handler needs it
    }
  };
  return handler;
};

/**
 * onEnter will run the provided function if Enter is pressed on this element.
 * @param eventHandler The onclick function pressing enter should run
 */
export function onEnter<El = HTMLInputElement>(eventHandler: (event: KeyboardEvent<El>) => void) {
  return (event: KeyboardEvent<El>) => {
    if (event.key === 'Enter') {
      eventHandler(event);
    }
  };
}

/**
 * Create an option for react-select
 */
export function selectOptionFactory<T = string>(value: T, label?: string): SelectOption<T> {
  return {
    label: label || value.toString(),
    value,
  };
}

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);
/**
 * Creates a string like "Add another thing" if there is more than one item,
 * and "Add a thing" if there are no items yet.
 */
export const getAddAnotherText = (noun: string, count: number) => {
  const startsWithVowel = VOWELS.has(noun.charAt(0).toLowerCase());
  const article = startsWithVowel ? 'an' : 'a';
  const another = count > 0 ? 'another' : article;
  return `Add ${another} ${noun}`;
};

export const getDepthByAccessLevel = (accessLevel?: OrganizationAccessLevel) => accessLevel === 'premium' ? LOD_OPTIONS.premium.accessDepth : LOD_OPTIONS.free.accessDepth;

/**
 * Check if a license match is declared/concluded
 */
const checkLicenseMatchPath = (path: string) => (match: Pick<Match, 'path'>) => match.path === path;

export const isDeclaredLicense = checkLicenseMatchPath('DECLARED_LICENSE');

export const isConcludedLicense = checkLicenseMatchPath('CONCLUDED_LICENSE');

/**
 * Join an array with ' OR '. Useful when concatenating multi-licenses.
 * Can be uppercase or lowercase, defaults to upper as that is more common.
 */
export const joinOr = (arr: string[], options = { uppercase: true }) => arr.join(options.uppercase ? ' OR ' : ' or ');

/**
 * Helper method for creating multi-license titles. Concats group.licenseId with ' OR '
 */
const getLicenseIdOrTitle = ({ title, licenseId }: RevisionLicense): (string | undefined) => {
  if (isCustomLicense(licenseId ?? '')) {
    return title || licenseId;
  }
  return licenseId || title;
};

/**
 * Render the complete license name for a license group. The license names are output sorted in ascending ASCII order.
 */
export const getMultiLicenseTitle = (group: RevisionLicense[], options = { uppercase: true }) =>
  joinOr(_.compact(group.map(getLicenseIdOrTitle)).sort(), options);

/**
 * For opening dynamic links in a new tab.
 * Note: try to use the generic `Link` component instead, if possible.
 */
export const openInNewTab = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

/**
 * Takes this.props.params and returns the base URL for a project
 * Looks something like: `/projects/${projectId}/refs/${refType}/${ref}/${revisionId}`
 */
export const getProjectBaseURLFromParams = ({
  project_id,
  reference_type,
  reference,
  revision_id,
}: RevisionRouteParams) =>
  `/projects/${[
    nuc(project_id),
    'refs',
    nuc(reference_type),
    encodeReference(reference),
    nuc(revision_id),
  ].join('/')}`;

/**
 * Takes this.props.params and returns the URL for a specific full page issue in a project
 * Looks something like:
 *   `/projects/${projectId}/refs/${refType}/${ref}/${revisionId}/issues/${issueCategory}/${issueId}`
 */
type GetProjectIssuesFullPageURLFromParams = {
  projectId: string;
  referenceType: string;
  reference: string;
  revisionId: string;
  issueCategory: Category;
  issueId: string;
};
export const getProjectIssuesFullPageURLFromParams = ({
  projectId,
  referenceType,
  reference,
  revisionId,
  issueCategory,
  issueId,
}: GetProjectIssuesFullPageURLFromParams) =>
  `/projects/${[
    nuc(projectId),
    'refs',
    nuc(referenceType),
    encodeReference(reference),
    nuc(revisionId),
    'issues',
    nuc(issueCategory),
    nuc(issueId),
  ].join('/')}`;

/**
 * Wraps an item in a JSX div
 * @param {string | ReactElement} item - Node that you want to wrap in a div
 * @returns {ReactElement}
 */
export const wrapWithDiv = (item: ReactNode, key: string | number = ''): ReactElement => (
  <div key={key}>{item}</div>
);

/**
 * Takes a list and splits at n index.
 * Returns a tuple where with two new arrays, from the split point.
 *
 * @param {any[]} list
 * @param {number} n - Number of items you want to include
 * @returns {[any[], any[]]} [first[], rest[]] - Two arrays, the first half and then the rest
 */
export const splitArray = <T,>(list: T[] = [], n: number = 1): [T[], T[]] => ([
  list.slice(0, n),
  list.slice(n),
]);

/**
 * Make the timestamp human readable
 * @param {string | Date} timestamp
 */
export const formatTimestamp = (timestamp: string | Date) =>
  moment(timestamp).format('MM/DD/YY h:mm a');

/**
 * "Hard" redirect (ie: page refreshes). Usually we prefer using router.push, but in some cases we want
 * to do a hard redirect to completely refresh the client-side state. Can also be used for
 * navigating to external pages (although links are prefered).
 *
 * @param {string} path - Path to redirect to.
 * @example
 * <button onClick={() => hardRedirectTo('/settings')}>
 *   Go to settings
 * </button>
 */
export const hardRedirectTo = (path: string = '/') => {
  window.location.href = path;
};

/**
 * Counts the number of a given boolean value in a given list.
 *
 * @param {boolean[]} list - List of boolean values.
 * @param {boolean} [test] - Value to count (`true` or `false`).
 * @returns {number}
 */
export function countBooleanValues(list: boolean[], test: boolean = true): number {
  return lodash.sumBy(list, value => value === test ? 1 : 0);
}

/**
 * Drop an array element at a given index.
 * If a negative index is provided, nothing is removed.
 * Returns a new array.
 *
 * @param {any[]} array - Any array.
 * @param {number} index - Index you want to remove from the array.
 */
export function dropAt<T>(array: T[], index: number): T[] {
  if (index < 0) {
    return array;
  }
  return [
    ...array.slice(0, index),
    ...array.slice(index + 1),
  ];
}

/**
 * Convert a number of bytes to a human readable string (i.e. 1000 -> 1 kb)
 */
export function getHumanReadableBytes(bytes: number, numberOfDecimals?: number) {
  if (bytes === 0) {
    return '0 Bytes';
  }

  const k = 1024;
  const dm = numberOfDecimals || 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * billingLevel determines the billing level of the account associated with the user.
 */
export const billingLevel = () => {
  if ((window.user.organization.access_level === 'premium' && window.user.organization.custom_billing) || window.on_prem) {
    return 'Paid (Premium Enterprise)';
  } if (window.user.organization.access_level === 'premium' && !window.user.organization.custom_billing) {
    return 'Paid (Self Serve)';
  } if (window.user.organization.access_level === 'free' && !window.user.organization.custom_billing) {
    return 'Free (Self Serve)';
  }
  // Default value
  return 'Unknown';
};

type UserOrgRole = 'Admin' | 'Editor' | 'Viewer' | 'Custom' | 'None';

/**
 * Since organizations can create Custom roles, we can't 100% determine if their Organization role is 1:1 with what we
 * call an "Admin"/"Editor"/"Viewer". Because of this, we determine the users role based on the existense of certain permissions
 * that we believe are "Admin". For example if the permission for managing roles, or editing billing settings exists, then the user
 * is likely an Admin. If they don't have those permissions, but can create policies, or delete ANY project, they are likely an editor.
 * If they don't have those permissions, but can view any project or policy in the org, they're likely a Viewer. If they have NONE of those,
 * but have an Organization Role (hasOrganizationRole), we assume it's a 'Custom' role. If they have no Organization role, then we return 'None'
 *
 */
export function getUserOrganizationRole(
  permissions: Permission[],
  hasOrganizationRole: boolean,
): UserOrgRole {
  let hasAdminRole = false;
  let hasEditorRole = false;
  let hasViewerRole = false;

  permissions.forEach((permission) => {
    // If we've already determined that they are an admin, we can short circuit
    if (hasAdminRole) {
      return;
    }

    // If they can manage user roles and/or edit billing settings, they are an Admin
    if (
      (permission.action === Action.Manage && permission.resourceType === ResourceType.Role) ||
      (permission.action === Action.Edit && permission.resourceType === ResourceType.OrganizationBillingSettings)
    ) {
      hasAdminRole = true;
      return;
    }

    // If they can create licensing/security policy or delete any project, they have permissions that an Editor typically has
    if (
      (permission.action === Action.Create && permission.resourceType === ResourceType.LicensingPolicy) ||
      (permission.action === Action.Create && permission.resourceType === ResourceType.SecurityPolicy) ||
      (permission.action === Action.Delete && permission.resourceType === ResourceType.ProjectAny)
    ) {
      hasEditorRole = true;
      return;
    }

    // If they can view any licensing/security policies and/or view any project, they have permissions that a Viewer typically has
    if (
      (permission.action === Action.View && permission.resourceType === ResourceType.LicensingPolicyAny) ||
      (permission.action === Action.View && permission.resourceType === ResourceType.SecurityPolicyAny) ||
      (permission.action === Action.View && permission.resourceType === ResourceType.ProjectAny)
    ) {
      hasViewerRole = true;
    }
  });

  if (hasAdminRole) {
    return 'Admin';
  }
  if (hasEditorRole) {
    return 'Editor';
  }
  if (hasViewerRole) {
    return 'Viewer';
  }

  // If the user has an organization role, we just weren't able to classify it, so we call it 'Custom'. If we can't classify it AND there's no org role, we determine that
  // there role is None
  return hasOrganizationRole ? 'Custom' : 'None';
}

/**
 * Takes a value (usually a string) and casts it to a number, returning
 * it only if it's actually a finite number.
 *
 * @param {unknown} maybeNumber - Value which might be a number.
 * @returns {number | undefined}
 */
export const toFiniteNumber = (maybeNumber: unknown): number | undefined => {
  const value = Number(maybeNumber);
  if (!Number.isFinite(value)) {
    return undefined;
  }
  return value;
};

/**
 * Simple function for displaying Ignore or Resolve based on a Feature Flag. This was a very particular request from a customer.
 * Don't ask questions.
 * @returns "Ignore" or "Resolve"
 */
export const ignoreOrResolveWord = () => isFeatureEnabled(IGNORE_RESOLVE_RENAME) ? 'Resolve' : 'Ignore';

/**
 * Simple function for displaying Ignored or Resolved based on a Feature Flag. This was a very particular request from a customer.
 * Don't ask questions.
 * @returns "Ignored" or "Resolved"
 */
export const ignoreOrResolveWordPast = () => `${ignoreOrResolveWord()}d`;

/**
 * Simple function for displaying Ignoring or Resolving based on a Feature Flag. This was a very particular request from a customer.
 * Don't ask questions.
 * @returns "Ignoring" or "Resolving"
 */
export const ignoreOrResolveWordActive = () => isFeatureEnabled(IGNORE_RESOLVE_RENAME) ? 'Resolving' : 'Ignoring';

/**
 * Modulo operation that appropriately handles negative numbers.
 *
 * Based on:
 * https://stackoverflow.com/a/17323608
 */
export const mod = (n: number, m: number) => {
  return ((n % m) + m) % m;
};

/**
 * Groups licenses by their `licenseGroupId` and then returns a list of grouped license IDs,
 * each group is a string of license IDs joined by " OR ".
 */
export const getLicenseGroups = (licenses: Array<Pick<RevisionLicenseModel, 'licenseGroupId' | 'licenseId'>>) => {
  const licenseGroups = _.groupBy(licenses, 'licenseGroupId');

  return Object.values(licenseGroups).map((licenseGroup) => {
    const text = licenseGroup
      .map((license) => license.licenseId)
      .sort()
      .join(' OR ');

    return {
      text,
      licenseGroup,
    };
  });
};

/**
 * Takes an EPSS score + percentile and returns a human-readable
 * label (which displays the score and percentile rank) as well as
 * a more detailed explanation (for tooltips, mainly).
 *
 * The label format is based on the official guidance described here:
 * https://www.first.org/epss/articles/prob_percentile_bins
 */
export const getHumanReadableEPSS = (epss: EpssResponse): { label: string, explanation: string } => {
  const {
    score: scoreWithSuffix,
    percentile: percentileWithSuffix,
    label,
  } = getHumanReadableEPSSFields(epss.percentile, epss.score);

  const explanation = `
    This vulnerability has an EPSS exploit likelihood of ${scoreWithSuffix}
    and ranks in the ${percentileWithSuffix} percentile when compared to
    other vulnerabilities.
  `;

  return { label, explanation };
};

/**
 * Check if a specific permission exists within a given list of permissions.
 *
 * This helper was extracted (verbatim) from "/views/layouts/common_layout.html".
 * Since it was originally untyped, I've decided to leave some of the "redundant"
 * null fallbacks in place.
 */
export const hasPermission = (permissions: Permission[], permission: Permission): boolean => {
  const resourceType = permission.resourceType || null;
  const action = permission.action || null;
  const projectId = permission.projectId || null;
  const releaseGroupId = permission.releaseGroupId || null;
  const teamId = permission.teamId || null;

  return permissions.some(p => (
    p.resourceType === resourceType &&
    p.action === action &&
    (p.projectId || null) === projectId &&
    (p.releaseGroupId || null) === releaseGroupId &&
    (p.teamId || null) === teamId
  ));
};

/**
 * Generate a cryptographically-secure random string (uses Browser crypto under the hood).
 *
 * References:
 * https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues
 * https://developer.mozilla.org/en-US/docs/Glossary/Base64#the_unicode_problem
 *
 * @param size Number of random bytes to include in the string (will NOT be the string's length)
 * @returns Random bytes represented as a base64-encoded, URL-safe string
 */
export const generateSecret = (size: number = 32) => {
  const buffer = new Uint8Array(size);
  window.crypto.getRandomValues(buffer);

  const isUrlSafe = true;
  return Base64.fromUint8Array(buffer, isUrlSafe);
};

/**
 * Rounds a number to the nth decimal place (default 2). This is useful
 * when dealing with JavaScript's famously bad floating point arithmetic.
 *
 * Example: roundToNthDecimalPlace(29.520000000000003, 2) => 1.23
 *
 * @param {number} value - The number to round.
 * @param {number} decimalPlaces - The number of decimal places to round to.
 */
export function roundToNthDecimalPlace(value: number, decimalPlaces: number = 2): number {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
}

/**
 * Given a single value or array of something, ensure it becomes an array.
 * In the "just a value" case, the result is a single-element array. If the
 * value is already an array, then it is returned as-is.
 * @param value Value that might be an array.
 * @returns An array of values.
 */
export function ensureArray<T>(value: T | T[]): T[] {
  if (Array.isArray(value)) {
    return value;
  }
  return [value];
}

export default {
  hashColor,
  isAuthed,
  defer,
  stopEventPropagation,
  containEvent,
  withTargetChecked,
  withClipboardText,
  onEnter,
  boldWords,
  numSuffix,
  trimString,
  truncateNumber,
  verify_subscription,
  capitalizeWord,
  stripHtml,
  nuc,
  withEscKeyPress,
  selectOptionFactory,
  getAddAnotherText,
  getDepthByAccessLevel,
  isDeclaredLicense,
  isConcludedLicense,
  joinOr,
  getMultiLicenseTitle,
  openInNewTab,
  getProjectBaseURLFromParams,
  getUserOrganizationRole,
  wrapWithDiv,
  splitArray,
  hardRedirectTo,
  countBooleanValues,
  dropAt,
  getHumanReadableBytes,
  toFiniteNumber,
  mod,
  isProjectInCurrentOrg,
  roundToNthDecimalPlace,
  ensureArray,
};
